/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetJsonWebKeySetDeletePreflightRequest,
  GetJsonWebKeySetDeletePreflightSecurity,
} from "../models/operations/getjsonwebkeysetdeletepreflight.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildJsonWebKeySetDeletePreflightQuery,
  JsonWebKeySetDeletePreflightQueryData,
  prefetchJsonWebKeySetDeletePreflight,
  queryKeyJsonWebKeySetDeletePreflight,
} from "./jsonWebKeySetDeletePreflight.core.js";
export {
  buildJsonWebKeySetDeletePreflightQuery,
  type JsonWebKeySetDeletePreflightQueryData,
  prefetchJsonWebKeySetDeletePreflight,
  queryKeyJsonWebKeySetDeletePreflight,
};

export type JsonWebKeySetDeletePreflightQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getSetDeletePreflight jsonWebKeySets
 *
 * @remarks
 * Authoritative impact summary for deleting a JSON Web Key Set: the remote_session_clients still referencing it. deleteSet returns a conflict for exactly the sets this reports a non-zero client_count for. Requires org:read.
 */
export function useJsonWebKeySetDeletePreflight(
  request: GetJsonWebKeySetDeletePreflightRequest,
  security?: GetJsonWebKeySetDeletePreflightSecurity | undefined,
  options?: QueryHookOptions<
    JsonWebKeySetDeletePreflightQueryData,
    JsonWebKeySetDeletePreflightQueryError
  >,
): UseQueryResult<
  JsonWebKeySetDeletePreflightQueryData,
  JsonWebKeySetDeletePreflightQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildJsonWebKeySetDeletePreflightQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getSetDeletePreflight jsonWebKeySets
 *
 * @remarks
 * Authoritative impact summary for deleting a JSON Web Key Set: the remote_session_clients still referencing it. deleteSet returns a conflict for exactly the sets this reports a non-zero client_count for. Requires org:read.
 */
export function useJsonWebKeySetDeletePreflightSuspense(
  request: GetJsonWebKeySetDeletePreflightRequest,
  security?: GetJsonWebKeySetDeletePreflightSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    JsonWebKeySetDeletePreflightQueryData,
    JsonWebKeySetDeletePreflightQueryError
  >,
): UseSuspenseQueryResult<
  JsonWebKeySetDeletePreflightQueryData,
  JsonWebKeySetDeletePreflightQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildJsonWebKeySetDeletePreflightQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setJsonWebKeySetDeletePreflightData(
  client: QueryClient,
  queryKeyBase: [parameters: { id: string; gramSession?: string | undefined }],
  data: JsonWebKeySetDeletePreflightQueryData,
): JsonWebKeySetDeletePreflightQueryData | undefined {
  const key = queryKeyJsonWebKeySetDeletePreflight(...queryKeyBase);

  return client.setQueryData<JsonWebKeySetDeletePreflightQueryData>(key, data);
}

export function invalidateJsonWebKeySetDeletePreflight(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { id: string; gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "jsonWebKeySets",
      "getDeletePreflight",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllJsonWebKeySetDeletePreflight(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "jsonWebKeySets", "getDeletePreflight"],
  });
}
